/**
 * The toolbar's global Models selection, translated into what an API request wants.
 *
 * The backend reads a missing/empty model list as "every model"
 * (`BuildingProjectHelper.GetMaterialsForModels`), and a present list as "only materials whose
 * branch name is in it" — which is NOT the same thing: a material with no branch name at all is
 * dropped by an explicit list and kept by an absent one. So a selection that covers the whole
 * project has to be sent as `undefined` rather than spelled out, or widening the filter back to
 * "all" would quietly lose those materials.
 *
 * The "covers everything" shortcut needs a known model list to compare against: while the dashboard
 * store's `models` is still empty the selection is sent verbatim, so an unloaded list can never
 * widen a request past the models the user is looking at.
 */
export const modelFilterForRequest = (
  allModelNames: string[] | null | undefined,
  selectedModelNames: string[] | null | undefined
): string[] | undefined => {
  const selected = selectedModelNames ?? [];
  if (selected.length === 0) return undefined;

  const projectModelCount = (allModelNames ?? []).length;
  if (projectModelCount > 0 && selected.length >= projectModelCount) return undefined;

  return [...selected];
};

/**
 * The selection, restricted to the models the project actually still has.
 *
 * The selection is persisted per project (`gaiup-<projectId>-dashboard-selectedModels`) and nothing
 * pruned it when Settings → Models removed a model, so a removed name survived every later load
 * (RTLCA-5788). Everything downstream reads the two lists as a pair and disagrees once they drift:
 * the flyout header counts the selection against the project's models ("6 of 1"), "Select all" and
 * the last-model lock compare sizes, and `modelFilterForRequest` above reads a stale count as
 * "covers everything" and widens the request back to all models.
 *
 * Two guards, both erring towards leaving the user's selection alone:
 *
 * - An empty or nullish model list means the list has not loaded yet, not that the project has no
 *   models — same reasoning as the "covers everything" shortcut above, which never fires on an
 *   unknown list either. The selection is handed back untouched.
 * - When nothing survives, the result is every model rather than none. An empty selection means
 *   "all" on the wire, but the flyout would render it as "0 of 1" against its own "at least one
 *   model must be selected" rule — so "all" is spelled out. This is the case that hits when the
 *   models that were removed are the only ones that had been ticked.
 *
 * Order is the selection's own, so a reconciled list that equals the selection compares equal
 * element-wise and costs no refetch.
 */
export const reconcileSelectedModels = (
  allModelNames: string[] | null | undefined,
  selectedModelNames: string[] | null | undefined
): string[] => {
  const selected = selectedModelNames ?? [];
  const allModels = allModelNames ?? [];

  if (allModels.length === 0) return [...selected];

  const existingModels = new Set(allModels);
  const survivingSelection = selected.filter((name) => existingModels.has(name));

  return survivingSelection.length > 0 ? survivingSelection : [...allModels];
};

// Appends models that are new since `previousModelNames` when every previous model was selected,
// so "all models" keeps meaning all; a narrowed selection, or an unknown previous list, is left alone.
export const withNewlyAddedModels = (
  previousModelNames: string[] | null | undefined,
  currentModelNames: string[] | null | undefined,
  selectedModelNames: string[] | null | undefined
): string[] => {
  const selected = selectedModelNames ?? [];
  const previous = previousModelNames ?? [];
  if (previous.length === 0) return [...selected];

  const selectedSet = new Set(selected);
  if (!previous.every((name) => selectedSet.has(name))) return [...selected];

  const previousSet = new Set(previous);
  const added = (currentModelNames ?? []).filter((name) => !previousSet.has(name) && !selectedSet.has(name));
  return [...selected, ...added];
};

/**
 * A stable string for watching the selection — two selections that produce the same request produce
 * the same key, so a watcher keyed on it refetches when the wire changes and stays quiet otherwise.
 *
 * Sorted, because the selection is a list the user builds by ticking boxes: the same set of models
 * can arrive in a different order (`toggleModel` appends, `Models.vue` applies a Set) without the
 * request meaning anything different.
 */
export const modelFilterRequestKey = (
  allModelNames: string[] | null | undefined,
  selectedModelNames: string[] | null | undefined
): string => (modelFilterForRequest(allModelNames, selectedModelNames) ?? []).slice().sort().join(" ");
